/**
 * Bounded compiler-session connection lifecycle.
 * Single in-flight init per project/session key; max 3 automatic attempts with a 5s delay.
 */

export const SESSION_MAX_ATTEMPTS = 3
export const SESSION_RETRY_DELAY_MS = 5000

export type SessionFailureKind =
  | "transient"
  | "auth"
  | "conflict"
  | "rate_limit"
  | "not_found"
  | "permanent"

export type SessionAttemptOutcome = "connected" | "disconnected" | "auth" | "aborted"

const activeInitLocks = new Map<string, string>()

export class SessionInitError extends Error {
  readonly kind: SessionFailureKind
  readonly status: number

  constructor(message: string, kind: SessionFailureKind, status: number) {
    super(message)
    this.name = "SessionInitError"
    this.kind = kind
    this.status = status
  }
}

export function classifyHttpStatus(status: number): SessionFailureKind {
  if (status === 401 || status === 403) return "auth"
  if (status === 404) return "not_found"
  if (status === 409) return "conflict"
  if (status === 429) return "rate_limit"
  if (status === 0 || status >= 500) return "transient"
  if (status >= 400) return "permanent"
  return "transient"
}

export function isRetryableSessionFailure(kind: SessionFailureKind): boolean {
  return kind === "transient" || kind === "rate_limit" || kind === "conflict"
}

export function isAbortError(error: unknown): boolean {
  return (
    (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  )
}

function readErrorStatus(error: unknown): number {
  if (error instanceof SessionInitError) return error.status
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status: unknown }).status
    if (typeof status === "number" && Number.isFinite(status)) return status
  }
  return 0
}

export function classifySessionError(error: unknown): SessionFailureKind {
  if (error instanceof SessionInitError) return error.kind
  return classifyHttpStatus(readErrorStatus(error))
}

export function acquireSessionInitLock(key: string, ownerId: string): boolean {
  const current = activeInitLocks.get(key)
  if (current && current !== ownerId) return false
  activeInitLocks.set(key, ownerId)
  return true
}

export function releaseSessionInitLock(key: string, ownerId: string): void {
  if (activeInitLocks.get(key) === ownerId) {
    activeInitLocks.delete(key)
  }
}

export function waitMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"))
      return
    }
    const timer = setTimeout(() => {
      if (signal) signal.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(new DOMException("Aborted", "AbortError"))
    }
    signal?.addEventListener("abort", onAbort, { once: true })
  })
}

export async function runBoundedSessionAttempts(options: {
  signal: AbortSignal
  isCurrent?: () => boolean
  maxAttempts?: number
  delayMs?: number
  onAttempt: (attempt: number, maxAttempts: number) => void
  onRetryWait?: (failedAttempt: number, delayMs: number) => void
  attempt: () => Promise<void>
}): Promise<SessionAttemptOutcome> {
  const maxAttempts = options.maxAttempts ?? SESSION_MAX_ATTEMPTS
  const delayMs = options.delayMs ?? SESSION_RETRY_DELAY_MS

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (options.signal.aborted || options.isCurrent?.() === false) return "aborted"
    options.onAttempt(attempt, maxAttempts)
    try {
      await options.attempt()
      return "connected"
    } catch (error) {
      if (isAbortError(error) || options.signal.aborted) return "aborted"
      const kind = classifySessionError(error)
      if (kind === "auth") return "auth"
      const canRetry = isRetryableSessionFailure(kind) && attempt < maxAttempts
      if (!canRetry) return "disconnected"
      options.onRetryWait?.(attempt, delayMs)
      try {
        await waitMs(delayMs, options.signal)
      } catch {
        return "aborted"
      }
    }
  }

  return "disconnected"
}
